import { Injectable } from '@angular/core';
import { SqliteStorage } from 'src/app/common/storage/sqlite-storage';

import { DatabaseService } from '../../services/database.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { FtsIndexFinalizer as Finalizer } from '../contracts/sync/sync-endpoints';
import { IndexRebuilder } from './index-rebuilder';

/**
 * Runs after the data sync (SyncStarter / WebSyncStarter) and before the sync indicator closes.
 *
 * During the initial sync the work item skips per-item FTS/search-index writes (that per-row cost
 * dominates the first bulk load). As a result the affected FTS tables end up incomplete
 * (ftsLength < tableLength), which `indexHealth()` reports as unhealthy. This element rebuilds those
 * tables in one bulk `INSERT ... SELECT` via `repairIndex()`.
 *
 * Because it sits before `SyncIndicatorCloser` in the sync order, the indicator can only close once
 * every index has been fully rebuilt - the search is never exposed in a half-built state.
 */
@Injectable()
export class FtsIndexFinalizer implements ISyncElement {
  public constructor(private _databaseService: DatabaseService) {}

  public get name(): string {
    return Finalizer;
  }

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    const result: SyncElementResult = { jobDone: false, contextId: context.id };

    const tablesToRebuild = await this.unhealthyTables();
    if (!tablesToRebuild.length) {
      result.jobDone = true;
      return result;
    }

    // Mirror IndexRebuilder: signal that an index rebuild is in progress so other parts of the app
    // (e.g. platform-sync canBeSynced) treat the index as not-yet-ready while we rebuild.
    IndexRebuilder.isRepairing = true;
    try {
      for (const storage of tablesToRebuild) {
        await storage.repairIndex();
      }
    } finally {
      IndexRebuilder.isRepairing = false;
    }

    result.jobDone = true;
    return result;
  }

  private async unhealthyTables(): Promise<SqliteStorage[]> {
    const tables: SqliteStorage[] = [];
    const storageMetadata = await this._databaseService.databaseNames;
    for (const metadata of storageMetadata) {
      const storage = await this._databaseService.getDatabase(metadata.databaseName);
      if (!storage.canRepairIndex) {
        continue;
      }
      const indexHealth = await storage.indexHealth();
      if (!indexHealth.healthy) {
        tables.push(storage as SqliteStorage);
      }
    }
    return tables;
  }
}
